import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heart, Share2, TrendingUp, Users, Zap } from "lucide-react";
import { useState } from "react";

const companies = [
  {
    id: "tesla",
    name: "Tesla",
    icon: "⚡",
    tagline: "Accelerating sustainable energy",
    followers: 2450000,
    status: "Public",
    founded: 2003,
    ceo: "Elon Musk",
    overview: "Transforming transportation and energy through electric vehicles and renewable solutions.",
    products: [
      { name: "Model S", type: "Sedan", status: "Production" },
      { name: "Model 3", type: "Sedan", status: "Production" },
      { name: "Cybertruck", type: "Truck", status: "Ramping" },
      { name: "Semi", type: "Commercial", status: "Production" },
    ],
    milestones: [
      { date: "2024 Q4", event: "Record quarterly deliveries", impact: "High" },
      { date: "2024 Q3", event: "FSD Beta v12 release", impact: "High" },
      { date: "2024 Q2", event: "Energy business surge", impact: "Medium" },
    ],
    socialStats: [
      { label: "X Followers", value: "148M" },
      { label: "YouTube Subs", value: "2.8M" },
      { label: "Reddit", value: "1.2M" },
    ],
  },
  {
    id: "spacex",
    name: "SpaceX",
    icon: "🚀",
    tagline: "Making humanity multiplanetary",
    followers: 1240000,
    status: "Private",
    founded: 2002,
    ceo: "Elon Musk",
    overview: "Developing advanced rockets and spacecraft for space exploration and satellite internet.",
    products: [
      { name: "Falcon 9", type: "Rocket", status: "Operational" },
      { name: "Falcon Heavy", type: "Heavy Lift", status: "Operational" },
      { name: "Starship", type: "Super Heavy", status: "Development" },
      { name: "Starlink", type: "Satellite Internet", status: "Expanding" },
    ],
    milestones: [
      { date: "2024 Q4", event: "IFT-6 test flight", impact: "Critical" },
      { date: "2024 Q3", event: "100th Falcon 9 launch", impact: "High" },
      { date: "2024 Q2", event: "5 Billion Starlink revenue", impact: "High" },
    ],
    socialStats: [
      { label: "X Followers", value: "87M" },
      { label: "YouTube Subs", value: "4.2M" },
      { label: "Reddit", value: "890K" },
    ],
  },
  {
    id: "starlink",
    name: "Starlink",
    icon: "🛰️",
    tagline: "Global high-speed internet",
    followers: 890000,
    status: "Private",
    founded: 2015,
    ceo: "Elon Musk",
    overview: "Providing low-latency broadband internet via satellite constellation worldwide.",
    products: [
      { name: "Internet Service", type: "Satellite ISP", status: "Live" },
      { name: "Starlink Mini", type: "Terminal", status: "Available" },
      { name: "Starlink Pro", type: "High Performance", status: "Available" },
      { name: "Maritime", type: "Ocean Connectivity", status: "Beta" },
    ],
    milestones: [
      { date: "2024 Q4", event: "1M+ active users", impact: "High" },
      { date: "2024 Q3", event: "Global coverage expansion", impact: "High" },
      { date: "2024 Q2", event: "Maritime service launch", impact: "Medium" },
    ],
    socialStats: [
      { label: "X Followers", value: "3.1M" },
      { label: "YouTube Subs", value: "1.8M" },
      { label: "Reddit", value: "456K" },
    ],
  },
  {
    id: "neuralink",
    name: "Neuralink",
    icon: "🧠",
    tagline: "Brain-computer interface",
    followers: 420000,
    status: "Private",
    founded: 2016,
    ceo: "Elon Musk",
    overview: "Developing high-bandwidth brain-computer interfaces for medical and human enhancement.",
    products: [
      { name: "N1 Implant", type: "Neural Interface", status: "Clinical Trials" },
      { name: "Patient Support", type: "Software", status: "Active" },
      { name: "Research Platform", type: "Development", status: "Beta" },
    ],
    milestones: [
      { date: "2024 Q4", event: "Patient 2 implant success", impact: "Critical" },
      { date: "2024 Q3", event: "Motor control breakthrough", impact: "Critical" },
      { date: "2024 Q2", event: "Patient 1 progress update", impact: "High" },
    ],
    socialStats: [
      { label: "X Followers", value: "2.4M" },
      { label: "YouTube Subs", value: "950K" },
      { label: "Reddit", value: "234K" },
    ],
  },
  {
    id: "boringcompany",
    name: "The Boring Company",
    icon: "🕳️",
    tagline: "Solving urban traffic",
    followers: 230000,
    status: "Private",
    founded: 2016,
    ceo: "Elon Musk",
    overview: "Building underground transportation tunnels to eliminate surface traffic congestion.",
    products: [
      { name: "Las Vegas Loop", type: "Transit Tunnel", status: "Operational" },
      { name: "Hyperloop Dev", type: "High Speed Transit", status: "Research" },
      { name: "Tunnel Boring", type: "Equipment", status: "Operational" },
    ],
    milestones: [
      { date: "2024 Q4", event: "Vegas expansion phase 2", impact: "High" },
      { date: "2024 Q3", event: "1M+ passengers Vegas", impact: "High" },
      { date: "2024 Q2", event: "NYC tunnel planning", impact: "Medium" },
    ],
    socialStats: [
      { label: "X Followers", value: "1.2M" },
      { label: "YouTube Subs", value: "340K" },
      { label: "Reddit", value: "89K" },
    ],
  },
];

export default function CompanyHub() {
  const [selectedCompany, setSelectedCompany] = useState(companies[0]);
  const [liked, setLiked] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };

  return (
    <div className="space-y-8">
      {/* Company Selector */}
      <motion.div
        className="grid grid-cols-2 md:grid-cols-5 gap-3"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {companies.map((company) => (
          <motion.button
            key={company.id}
            variants={itemVariants}
            onClick={() => setSelectedCompany(company)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`p-4 border text-center transition-all ${
              selectedCompany.id === company.id
                ? "border-foreground bg-card"
                : "border-border hover:border-foreground/50"
            }`}
          >
            <div className="text-3xl mb-2">{company.icon}</div>
            <p className="text-sm font-medium text-foreground">
              {company.name}
            </p>
          </motion.button>
        ))}
      </motion.div>

      {/* Company Detail */}
      <motion.div
        key={selectedCompany.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="border border-border p-8"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-5xl">{selectedCompany.icon}</span>
              <div>
                <h1 className="text-3xl font-medium text-foreground">
                  {selectedCompany.name}
                </h1>
                <p className="text-muted-foreground">{selectedCompany.tagline}</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setLiked(!liked)}
              className="p-3 hover:bg-card transition-colors"
            >
              <Heart
                className={`h-5 w-5 ${
                  liked ? "fill-red-500 text-red-500" : "text-muted-foreground"
                }`}
              />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              className="p-3 hover:bg-card transition-colors"
            >
              <Share2 className="h-5 w-5 text-muted-foreground" />
            </motion.button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 pb-8 border-b border-border">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Status</p>
            <Badge variant="outline" className="text-xs">
              {selectedCompany.status}
            </Badge>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Founded</p>
            <p className="font-medium text-foreground">{selectedCompany.founded}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">CEO</p>
            <p className="font-medium text-foreground">{selectedCompany.ceo}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Community</p>
            <div className="flex items-center gap-1 text-foreground">
              <Users className="h-4 w-4" />
              <span className="text-sm font-medium">
                {(selectedCompany.followers / 1000000).toFixed(1)}M
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-4 border-b border-border bg-transparent">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="products">Products</TabsTrigger>
            <TabsTrigger value="milestones">Milestones</TabsTrigger>
            <TabsTrigger value="social">Social</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6 mt-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <p className="text-lg text-muted-foreground leading-relaxed">
                {selectedCompany.overview}
              </p>
            </motion.div>
          </TabsContent>

          <TabsContent value="products" className="space-y-4 mt-8">
            <motion.div
              className="space-y-3"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {selectedCompany.products.map((product, idx) => (
                <motion.div
                  key={idx}
                  variants={itemVariants}
                  className="border border-border p-4 hover:border-foreground/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-foreground">
                        {product.name}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {product.type}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {product.status}
                    </Badge>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </TabsContent>

          <TabsContent value="milestones" className="space-y-4 mt-8">
            <motion.div
              className="space-y-3"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {selectedCompany.milestones.map((milestone, idx) => (
                <motion.div
                  key={idx}
                  variants={itemVariants}
                  className="border border-border p-4 hover:border-foreground/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-mono text-muted-foreground mb-1">
                        {milestone.date}
                      </p>
                      <h4 className="font-medium text-foreground">
                        {milestone.event}
                      </h4>
                    </div>
                    <Badge
                      variant={
                        milestone.impact === "Critical"
                          ? "destructive"
                          : milestone.impact === "High"
                          ? "default"
                          : "secondary"
                      }
                      className="text-xs"
                    >
                      {milestone.impact}
                    </Badge>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </TabsContent>

          <TabsContent value="social" className="space-y-4 mt-8">
            <motion.div
              className="space-y-3"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {selectedCompany.socialStats.map((stat, idx) => (
                <motion.div
                  key={idx}
                  variants={itemVariants}
                  className="border border-border p-4 hover:border-foreground/50 transition-colors flex items-center justify-between"
                >
                  <span className="text-foreground font-medium">
                    {stat.label}
                  </span>
                  <span className="text-lg font-semibold text-foreground">
                    {stat.value}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </TabsContent>
        </Tabs>

        {/* CTA */}
        <div className="flex gap-3 mt-8 pt-8 border-t border-border">
          <Button className="bg-foreground text-background hover:bg-foreground/90">
            Follow Company
          </Button>
          <Button variant="outline" className="border-border">
            View News
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
